<template>
  <div class="bulk-scheduler">
    <div class="mb-6">
      <h4 class="text-lg font-medium text-slate-900 dark:text-white mb-2">
        {{ $t('BULK_BROADCAST.SCHEDULE.TITLE') }}
      </h4>
      <p class="text-sm text-slate-600 dark:text-slate-400">
        {{ $t('BULK_BROADCAST.SCHEDULE.DESCRIPTION') }}
      </p>
    </div>

    <!-- Selector de fecha y hora -->
    <div class="max-w-md">
      <label class="block text-sm font-medium text-slate-900 dark:text-white mb-2">
        {{ $t('BULK_BROADCAST.SCHEDULE.DATETIME_LABEL') }}
      </label>
      <input
        :value="modelValue"
        type="datetime-local"
        :min="minDateTime"
        :max="maxDateTime"
        class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        @input="$emit('update:modelValue', $event.target.value)"
      />
      <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
        {{ $t('BULK_BROADCAST.SCHEDULE.DATETIME_HELP') }}
      </p>
    </div>

    <!-- Error de validación -->
    <div
      v-if="validationError"
      class="mt-4 flex items-start space-x-2 text-sm text-red-600 dark:text-red-400"
    >
      <Icon icon="i-lucide-alert-circle" size="16" class="mt-0.5 flex-shrink-0" />
      <span>{{ validationError }}</span>
    </div>

    <!-- Doble horario: local del usuario y del servidor -->
    <div
      v-if="isValid"
      class="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4"
    >
      <div class="p-4 border border-slate-200 dark:border-slate-700 rounded-lg">
        <div class="flex items-center space-x-2 mb-1">
          <Icon icon="i-lucide-user" class="text-blue-500" size="16" />
          <h6 class="text-sm font-medium text-slate-900 dark:text-white">
            {{ $t('BULK_BROADCAST.SCHEDULE.LOCAL_TIME') }}
          </h6>
        </div>
        <p class="text-sm text-slate-700 dark:text-slate-300">{{ localTimeLabel }}</p>
        <p class="text-xs text-slate-500 dark:text-slate-400">{{ localTimezone }}</p>
      </div>

      <div class="p-4 border border-slate-200 dark:border-slate-700 rounded-lg">
        <div class="flex items-center space-x-2 mb-1">
          <Icon icon="i-lucide-server" class="text-violet-500" size="16" />
          <h6 class="text-sm font-medium text-slate-900 dark:text-white">
            {{ $t('BULK_BROADCAST.SCHEDULE.SERVER_TIME') }}
          </h6>
        </div>
        <p class="text-sm text-slate-700 dark:text-slate-300">{{ serverTimeLabel }}</p>
        <p class="text-xs text-slate-500 dark:text-slate-400">{{ serverTimezone }}</p>
      </div>
    </div>

    <!-- Resumen de lo que va a quedar agendado -->
    <div v-if="isValid" class="mt-6 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
      <div class="flex items-start space-x-3">
        <Icon icon="i-lucide-calendar-clock" class="text-blue-500 mt-0.5" size="20" />
        <div class="min-w-0">
          <h6 class="font-medium text-slate-900 dark:text-white">
            {{ taskTitle }}
          </h6>
          <p class="mt-1 text-sm text-slate-600 dark:text-slate-400">
            {{ taskDescription }}
          </p>
          <ul class="mt-3 space-y-1 text-sm text-slate-700 dark:text-slate-300">
            <li>
              <span class="text-slate-500 dark:text-slate-400">{{ $t('BULK_BROADCAST.SCHEDULE.TASK_NAME') }}:</span>
              <strong class="ml-1">{{ taskName }}</strong>
            </li>
            <li>
              <span class="text-slate-500 dark:text-slate-400">{{ $t('BULK_BROADCAST.SCHEDULE.CONTACTS') }}:</span>
              <strong class="ml-1">{{ totalContacts }}</strong>
            </li>
            <li v-if="templateLabel">
              <span class="text-slate-500 dark:text-slate-400">{{ $t('BULK_BROADCAST.SCHEDULE.TEMPLATE') }}:</span>
              <strong class="ml-1">{{ templateLabel }}</strong>
            </li>
            <li>
              <span class="text-slate-500 dark:text-slate-400">{{ $t('BULK_BROADCAST.SCHEDULE.PRIORITY') }}:</span>
              <strong class="ml-1">{{ $t('BULK_BROADCAST.SCHEDULE.PRIORITY_URGENT') }}</strong>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Aviso de granularidad del disparo -->
    <div v-if="isValid" class="mt-4 flex items-start space-x-2 text-xs text-slate-500 dark:text-slate-400">
      <Icon icon="i-lucide-info" size="14" class="mt-0.5 flex-shrink-0" />
      <span>{{ $t('BULK_BROADCAST.SCHEDULE.GRANULARITY_NOTE') }}</span>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  campaignName: {
    type: String,
    default: ''
  },
  totalContacts: {
    type: Number,
    default: 0
  },
  template: {
    type: Object,
    default: null
  },
  // 'reschedule' sólo cambia el copy del bloque de tarea; la validación es la misma.
  mode: {
    type: String,
    default: 'create'
  }
});

const emit = defineEmits(['update:modelValue', 'update:valid']);

const { t } = useI18n();

// Date.now() no es reactivo: sin este tick, `min` y la validación quedan congelados
// en el momento en que se montó el paso y una fecha vencida seguiría pareciendo válida.
const CLOCK_TICK_MS = 30 * 1000;
const now = ref(Date.now());
let clockTimer = null;

onMounted(() => {
  clockTimer = setInterval(() => {
    now.value = Date.now();
  }, CLOCK_TICK_MS);
});

onBeforeUnmount(() => {
  if (clockTimer) clearInterval(clockTimer);
});

// El servidor corre en UTC salvo que se configure otra zona; el layout la expone
// para no hardcodear el offset en el front.
const serverTimezone = computed(() => window.chatwootConfig?.serverTimezone || 'UTC');
// Identificador IANA real (Time.zone.tzinfo.identifier). Es el que entiende Intl y el
// único que resuelve el horario de verano de la fecha elegida, no el de hoy.
const serverTimezoneIana = computed(() => window.chatwootConfig?.serverTimezoneIana || '');
const serverOffsetMinutes = computed(() =>
  Number(window.chatwootConfig?.serverUtcOffsetMinutes ?? 0)
);

const localTimezone = computed(
  () => Intl.DateTimeFormat().resolvedOptions().timeZone || ''
);

const selectedDate = computed(() => {
  if (!props.modelValue) return null;
  const date = new Date(props.modelValue);
  return Number.isNaN(date.getTime()) ? null : date;
});

// Margen mínimo: el barrido corre cada 5 minutos, agendar "para ya" no tiene sentido.
const toInputValue = date => {
  const pad = value => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const minDateTime = computed(() => toInputValue(new Date(now.value + 5 * 60 * 1000)));

// Tope de un año, igual que BulkBroadcast::MAX_SCHEDULE_AHEAD en el backend.
const maxDate = computed(() => {
  const date = new Date(now.value);
  date.setFullYear(date.getFullYear() + 1);
  return date;
});

const maxDateTime = computed(() => toInputValue(maxDate.value));

const validationError = computed(() => {
  if (!props.modelValue) return t('BULK_BROADCAST.SCHEDULE.ERRORS.REQUIRED');
  if (!selectedDate.value) return t('BULK_BROADCAST.SCHEDULE.ERRORS.INVALID');
  if (selectedDate.value.getTime() <= now.value) {
    return t('BULK_BROADCAST.SCHEDULE.ERRORS.PAST');
  }
  if (selectedDate.value.getTime() > maxDate.value.getTime()) {
    return t('BULK_BROADCAST.SCHEDULE.ERRORS.TOO_FAR');
  }
  return '';
});

const isValid = computed(() => !validationError.value);

// El diálogo de reprogramar usa esto para habilitar su botón de confirmar.
watch(isValid, value => emit('update:valid', value), { immediate: true });

// Claves literales: el repo evita $t con interpolación de variables.
const taskTitle = computed(() =>
  props.mode === 'reschedule'
    ? t('BULK_BROADCAST.SCHEDULE.TASK_TITLE_RESCHEDULE')
    : t('BULK_BROADCAST.SCHEDULE.TASK_TITLE')
);

const taskDescription = computed(() =>
  props.mode === 'reschedule'
    ? t('BULK_BROADCAST.SCHEDULE.TASK_DESCRIPTION_RESCHEDULE')
    : t('BULK_BROADCAST.SCHEDULE.TASK_DESCRIPTION')
);

const formatDate = (date, timeZone) =>
  new Intl.DateTimeFormat(undefined, {
    dateStyle: 'full',
    timeStyle: 'short',
    ...(timeZone ? { timeZone } : {})
  }).format(date);

const localTimeLabel = computed(() =>
  selectedDate.value ? formatDate(selectedDate.value) : ''
);

// Fallback por offset fijo: sirve cuando no hay identificador IANA utilizable, pero
// usa el offset de hoy, así que puede errar una hora si el servidor tiene DST.
const serverTimeLabelFromOffset = () => {
  const shifted = new Date(
    selectedDate.value.getTime() + (serverOffsetMinutes.value + selectedDate.value.getTimezoneOffset()) * 60 * 1000
  );
  return formatDate(shifted);
};

const serverTimeLabel = computed(() => {
  if (!selectedDate.value) return '';

  if (serverTimezoneIana.value) {
    try {
      return formatDate(selectedDate.value, serverTimezoneIana.value);
    } catch (error) {
      // Zona desconocida para este navegador: se cae al cálculo por offset.
      return serverTimeLabelFromOffset();
    }
  }

  return serverTimeLabelFromOffset();
});

const taskName = computed(
  () => `Envio masivo: ${props.campaignName || t('BULK_BROADCAST.SCHEDULE.UNNAMED_CAMPAIGN')}`
);

const templateLabel = computed(() => {
  if (!props.template) return '';
  return props.template.name || props.template.id || '';
});
</script>
